import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getSettlementLinesApi } from "api/accounting";
import { Settlement, SettlementLine } from "interface/accounting";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";

interface SettlementsMobileListProps {
	items: Settlement[];
	loading: boolean;
	onOpenPdf: (item: Settlement) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (item: Settlement) => {
	if (item.invoiceSentDate) return "settlement-list-card--status-green";
	if (item.printedDate) return "settlement-list-card--status-yellow";
	return "settlement-list-card--status-red";
};

const formatAmount = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});
};

const SettlementsMobileList: FC<SettlementsMobileListProps> = ({
	items,
	loading,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [expandedSettlements, setExpandedSettlements] = useState<number[]>([]);
	const [lineMap, setLineMap] = useState<Record<number, SettlementLine[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<number, boolean>>({});

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	const toggleLines = (item: Settlement) => {
		if (expandedSettlements.includes(item.id)) {
			setExpandedSettlements((prev) => prev.filter((id) => id !== item.id));
			return;
		}

		setExpandedSettlements((prev) => [...prev, item.id]);
		if (lineMap[item.id] || lineLoadingMap[item.id]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.id]: true }));

		getSettlementLinesApi(String(item.id))
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.id]: (response?.data || []) as SettlementLine[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({ ...prev, [item.id]: false }));
			});
	};

	return (
		<div className="settlement-list-mobile">
			{empty && <div className="settlement-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const expanded = expandedSettlements.includes(item.id);
				const lines = lineMap[item.id] || [];
				const linesLoading = lineLoadingMap[item.id] === true;
				const itemKey = `${item.id}-${item.reportGuid ?? "noreport"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`settlement-list-card ${getStatusClassName(item)}`}
					>
						<div className="settlement-list-card-header">
							<div className="settlement-list-card-note">{item.invoiceNumber || item.id}</div>
							<div className="settlement-list-card-vessel">{item.customerName || "-"}</div>
							<div className="settlement-list-card-date">
								{item.invoiceDate ? (
									<GrideDateFormatter
										date={item.invoiceDate}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="settlement-list-card-body">
							<div>
								<span>{t("SettlementNumber")}</span>
								<strong>{item.invoiceNumber || "-"}</strong>
							</div>
							<div>
								<span>{t("Vessel")}</span>
								<strong>{item.customerName || "-"}</strong>
							</div>
							<div>
								<span>{t("PrintDate")}</span>
								<strong>
									{item.printedDate ? (
										<GrideDateFormatter
											date={item.printedDate}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									) : (
										"-"
									)}
								</strong>
							</div>
							<div>
								<span>{t("PrintedBy")}</span>
								<strong>{item.printedBy || "-"}</strong>
							</div>
							<div>
								<span>{t("PaymentDate")}</span>
								<strong>
									{item.dueDate ? (
										<GrideDateFormatter
											date={item.dueDate}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									) : (
										"-"
									)}
								</strong>
							</div>
							<div>
								<span>EHF</span>
								<strong>{item.invoiceSentDate ? t("Yes") : t("No")}</strong>
							</div>
							<div className="settlement-list-card-text-block">
								<span>{t("Value")}</span>
								<strong>{formatAmount(item.grossValue)}</strong>
							</div>
						</div>

						<div className="settlement-list-card-actions">
							<button
								type="button"
								className="settlement-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
							<button
								type="button"
								className="settlement-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="settlement-list-lines">
								{linesLoading && <div className="settlement-list-lines-loading">{t("Loading")}</div>}
								{!linesLoading && lines.length === 0 && (
									<div className="settlement-list-lines-empty">{t("NoRows")}</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.lineNumber ?? "noline"}-${line.articleNumber ?? "noarticle"}-${lineIndex}`}
											className="settlement-list-line-row"
										>
											<div className="settlement-list-line-number">#{line.lineNumber}</div>
											<div className="settlement-list-line-product">
												<div>{line.description1 || "-"}</div>
												<div className="settlement-list-line-meta">
													{t("Rate")}: {Number(line.exchangeRate || 0).toLocaleString("no-NO", {
														maximumFractionDigits: 2,
													})}
												</div>
											</div>
											<div className="settlement-list-line-summary">
												<strong>{formatAmount(line.invoicePrice)}</strong>
												<span>{t("BaseAmount")}: {formatAmount(line.costPrice)}</span>
												<span>{t("TotalThisYear")}: {formatAmount(line.totalThisYear)}</span>
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && <div className="settlement-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default SettlementsMobileList;